import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ConcessionStatus, FormatterData, MessageType, UserRoles } from '../constants/app.constants';
import { ApplicationResponseData, CompanyData, DateFormat, TechnicianData } from '../domains/common.domain';
import { LocalStorageService } from '../services/local-storage.service';
import { Companies } from '../model/companies';
import { RegistrationService } from '../registration/registration.service';
import {
  DataComponentApplicationDetails,
  TechnicianApplicationData,
  UpdateDataResponse,
  UpdateTechnicianApplication,
} from '../employee-overview/employee-overview.domain';
import { ValidationsService } from '../services/validations.service';
import { LookupOptionsData } from '../jurisdiction/jurisdiction.domain';
import { AllDropdownKeys } from '../jurisdiction/jurisdiction.constants';
import { CommonService } from '../services/common.service';
import { SubmitService } from '../services/submit.service';
import { Router } from '@angular/router';
import { ConfirmationService } from 'primeng';
import { EmployeeOverviewService } from '../employee-overview/employee-overview.service';
import { CommunicationService } from '../services/communication.service';
import { EnvironmentService } from '../services/environment.service';
import { Data } from '../interface/config.interface';
import { forkJoin } from 'rxjs';

@Component({
  providers: [ConfirmationService, EmployeeOverviewService],
  selector: 'app-data',
  styleUrls: ['./data.component.scss'],
  templateUrl: './data.component.html',
})
export class DataComponent implements OnChanges, OnInit {
  @Input() dataObject: {
    isFormReadonly: boolean;
    technicianData: TechnicianApplicationData;
    isEmployeeOverview: boolean;
  } = null;
  @Output() technicianApplicationDataChange: EventEmitter<DataComponentApplicationDetails>;

  companyFields: string[];
  technicianFields: string[];
  companyId: string;
  dateFormat: DateFormat;
  disableSubmitButton: boolean;
  isFormReadonly: boolean;
  myDataFormFields: Data;
  originalData: ApplicationResponseData;
  showActionButtons: boolean;
  technicianId: string;
  technicianInfoFormGroup: FormGroup;
  titles: LookupOptionsData[];
  titleKey: string;
  isLoading: boolean;
  showAuxiliaryOption: boolean;
  showGuildMember: boolean = this.environmentService.showGuildMember;
  showCompanyDescription: boolean = this.environmentService.showCompanyDescription;

  constructor(
    private commonService: CommonService,
    private communicationService: CommunicationService,
    private confirmationService: ConfirmationService,
    private employeeOverviewService: EmployeeOverviewService,
    private localStorageService: LocalStorageService,
    private registrationService: RegistrationService,
    private route: Router,
    private submitService: SubmitService,
    private environmentService: EnvironmentService
  ) {
    this.companyId = '';
    this.dateFormat = FormatterData.date;
    this.isFormReadonly = false;
    this.disableSubmitButton = true;
    this.titleKey = AllDropdownKeys.Title;
    this.titles = this.commonService.getDropdownData(this.titleKey);

    this.technicianApplicationDataChange = new EventEmitter<DataComponentApplicationDetails>();
  }

  ngOnInit(): void {
    this.isLoading = false;
    this.showActionButtons = !this.dataObject;
    const role = this.localStorageService.getItem('role');
    this.myDataFormFields = this.environmentService.myData;
    this.showAuxiliaryOption = this.environmentService.showAuxiliaryRadioButton;

    this.myDataFormFields.showSweepingDistrictNumber = role === 'chimney_sweeper' && this.myDataFormFields.showSweepingDistrictNumber;
    this.myDataFormFields.showTaxId = role !== 'chimney_sweeper' && this.myDataFormFields.showTaxId;
    this.myDataFormFields.showCompanyName = role !== 'chimney_sweeper' && this.myDataFormFields.showCompanyName;
    this.myDataFormFields.showCompanyTelephone = role !== 'chimney_sweeper' && this.myDataFormFields.showCompanyTelephone;
    this.myDataFormFields.showCeo = role !== 'chimney_sweeper' && this.myDataFormFields.showCeo;
    this.myDataFormFields.showHeadSubsidiary = role !== 'chimney_sweeper' && this.myDataFormFields.showHeadSubsidiary;
    this.myDataFormFields.showBirthday = role !== 'chimney_sweeper' && this.myDataFormFields.showBirthday;

    if (this.dataObject) {
      this.isFormReadonly = this.dataObject.isFormReadonly || this.dataObject.isEmployeeOverview;
      this.companyId = (this.dataObject.technicianData && this.dataObject.technicianData.company_id) || '';
      this.technicianId = (this.dataObject.technicianData && this.dataObject.technicianData.technician_id) || '';
      this.originalData = {
        technician_application: [this.dataObject.technicianData],
      };
      this.dataObject.technicianData = null;
      this.prepareForm();
    } else {
      this.technicianId = this.localStorageService.getItem('technician_id');
      this.getTechnicianDetails();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.dataObject && !changes.dataObject.firstChange) {
      this.prepareForm();
    }
  }

  getTechnicianDetails(): void {
    const subscription = this.employeeOverviewService.getTechnicianApplication(Number(this.technicianId));
    this.isLoading = true;
    subscription.subscribe((response: ApplicationResponseData) => {
      this.isLoading = false;
      this.companyId =
        (response.technician_application && response.technician_application[0] && response.technician_application[0].company_id) || '';

      if (response.technician_application[0]?.technician?.length && response.technician_application[0]?.technician[0]?.birthday) {
        response.technician_application[0].technician[0].birthday = this.commonService.parseDateForUI(
          response.technician_application[0].technician[0].birthday
        );
      }
      this.originalData = response;
      this.prepareForm();
    });
  }

  confirmDataChange(): void {
    const role = this.localStorageService.getItem('role') || '';
    if (this.dataObject) {
      this.updateTechnicianApplicationData();
    } else if (role === UserRoles.chimneySweeper) {
      this.saveTechnicianData();
    } else {
      this.confirmationService.confirm({
        accept: () => {
          this.saveTechnicianData();
        },
        reject: () => {
          this.confirmationService.close();
        },
      });
    }
  }

  /*updateLockStatus(): void {
   const applicationData = this.originalData.technician_application[0] || null;
   if (applicationData && applicationData.locked === '0') {
   this.isLoading = true;
   this.commonService.updateApplicationLockStatus(
   applicationData.locked === '0',
   applicationData.ta_id,
   null).subscribe((response: UpdateLockStatusResponse) => {
   this.isLoading = false;
   this.errorService.throwErrorIfFailure(response, true);
   this.saveTechnicianData();
   },
   (error) => {
   this.isLoading = false;
   this.errorService.throwErrorIfFailure(error, true);
   });
   } else {
   this.saveTechnicianData();
   }
   }*/

  private saveTechnicianData(): void {
    const applicationData = this.originalData.technician_application[0] || null;
    const currentFormData = this.technicianInfoFormGroup.value;

    if (currentFormData.birthday) {
      const birthday = currentFormData.birthday;
      currentFormData.birthday = this.commonService.parseDateForSaving(birthday);
    }

    if (this.technicianInfoFormGroup.valid) {
      this.isLoading = true;
      const requestData: TechnicianData = {
        last_name: currentFormData.last_name,
        technician_id: currentFormData.technician_id,
        first_name: currentFormData.first_name,
        birthday: currentFormData.birthday,
        sk_title: currentFormData.title || null,
        telephone: currentFormData.user_telephone,
        email: currentFormData.user_mail,
        meisterpruefung_bei: currentFormData.meisterpruefung_bei,
        meisterpruefung_date: currentFormData.meisterpruefung_date,
        ...(this.showGuildMember && { is_guild_member: currentFormData.is_guild_member ? 'true' : null }),
      };

      if (this.companyId) {
        requestData.company_id = this.companyId;
      }

      const saveSubscription = this.employeeOverviewService.updateTechnicianDetails(applicationData.technician_id, requestData);
      saveSubscription.subscribe(
        (response: UpdateDataResponse) => {
          if (response.result === 'updated' || !response.result) {
            const companyData = this.getCompanyData();
            const techApplicationData = {
              technician_application: [
                {
                  status: ConcessionStatus.renew_requested,
                },
              ],
            };

            forkJoin([
              this.employeeOverviewService.updateCompanyData(this.companyId, companyData),
              this.registrationService.updateTechnicianApplication(techApplicationData, applicationData.ta_id),
            ]).subscribe(
              () => {
                this.isLoading = false;
                this.submitService.setMessage(MessageType.CHANGES_SAVED);
                this.route.navigate(['/submitted']);
              },
              () => {
                this.isLoading = false;
                this.submitService.setMessage(MessageType.ERROR);
                this.route.navigate(['/submitted']);
              }
            );
          } else if (response.result === 'error') {
            this.submitService.setMessage(MessageType.ERROR);
            this.route.navigate(['/submitted']);
          }
        },
        () => {
          this.submitService.setMessage(MessageType.ERROR);
          this.route.navigate(['/submitted']);
        }
      );
    }
  }

  private getCompanyData(): CompanyData {
    const currentFormData = this.technicianInfoFormGroup.value;

    const companyData: CompanyData = {
      company_name: currentFormData.company_name,
      street: currentFormData.street,
      street_number: currentFormData.street_number,
      postcode: currentFormData.postcode,
      city: currentFormData.city,
      telephone: currentFormData.telephone,
      ceo: currentFormData.ceo,
      headoffice: currentFormData.headsubsidiary === 'hauptbetrieb' ? 'true' : 'false',
      subsidiary: currentFormData.headsubsidiary === 'nebenbetrieb' ? 'true' : 'false',
      ...(this.myDataFormFields.showSweepingDistrictNumber && {
        cs_district_number: currentFormData.sweeping_district_number,
      }),
      ...(this.showAuxiliaryOption && {
        auxiliary_company: currentFormData.headsubsidiary === 'auxiliary_company' ? 'true' : 'false',
      }),
      ...(this.showCompanyDescription && { company_description: currentFormData.company_description }),
    };

    return companyData;
  }

  resetFormData(): void {
    this.technicianInfoFormGroup.reset();
    this.prepareForm();
    this.disableSubmitButton = true;
  }

  getCompanyDetails(): void {
    const taxId = this.technicianInfoFormGroup.value.tax_id;
    if (taxId !== null && taxId !== '' && taxId !== undefined) {
      this.communicationService.getCompany(taxId).subscribe((companies: Companies) => {
        this.updateFormWithCompanyData(companies);
      });
    } else {
      this.updateFormWithCompanyData(null);
    }
  }

  private prepareForm(): void {
    let companyData: CompanyData;
    let technicianData: TechnicianData;

    if (this.originalData.technician_application?.length) {
      companyData = {
        ...(this.originalData.technician_application[0].company && this.originalData.technician_application[0].company[0]),
      };
      technicianData = {
        ...(this.originalData.technician_application[0].technician && this.originalData.technician_application[0].technician[0]),
      };
    }

    this.companyFields = [];
    this.technicianFields = [];
    this.technicianInfoFormGroup = new FormGroup({
      ...(this.myDataFormFields.showTaxId && {
        tax_id: this.createFormControl(companyData, 'tax_id'),
      }),
      ...(this.myDataFormFields.showSweepingDistrictNumber && {
        sweeping_district_number: this.createFormControl(companyData, 'cs_district_number'),
      }),
      ...(this.myDataFormFields.showCompanyName && {
        company_name: this.createFormControl(companyData, 'company_name'),
      }),
      street: this.createFormControl(companyData, 'street'),
      street_number: this.createFormControl(companyData, 'street_number'),
      postcode: this.createFormControl(companyData, 'postcode'),
      city: this.createFormControl(companyData, 'city'),
      ...(this.myDataFormFields.showCompanyTelephone && {
        telephone: this.createFormControl(companyData, 'telephone'),
      }),
      ...(this.myDataFormFields.showCeo && {
        ceo: this.createFormControl(companyData, 'ceo'),
      }),
      ...(this.myDataFormFields.showHeadSubsidiary && {
        headsubsidiary: this.createFormControl(companyData, 'headsubsidiary'),
      }),
      ...(this.showCompanyDescription && { company_description: this.createFormControl(companyData, 'company_description') }),
      title: this.createFormControl(technicianData, 'sk_title'),
      last_name: this.createFormControl(technicianData, 'last_name'),
      first_name: this.createFormControl(technicianData, 'first_name'),
      technician_id: this.createFormControl(technicianData, 'technician_id'),
      ...(this.myDataFormFields.showBirthday && {
        birthday: this.createFormControl(technicianData, 'birthday'),
      }),
      user_telephone: this.createFormControl(technicianData, 'telephone'),
      user_mail: this.createFormControl(technicianData, 'email'),
      ...(this.showGuildMember && { is_guild_member: this.createFormControl(technicianData, 'is_guild_member') }),
    });

    this.technicianInfoFormGroup.valueChanges.subscribe(() => {
      this.disableSubmitButton = this.technicianInfoFormGroup.invalid;
      if (this.dataObject) {
        this.updateTechnicianApplicationData();
      }
    });
    if (this.dataObject) {
      this.updateTechnicianApplicationData();
    }
  }

  private createFormControl(dataObject: CompanyData | TechnicianData, key: string): FormControl {
    let value: string;

    if (dataObject && dataObject.hasOwnProperty('company_id')) {
      this.companyFields.push(key);
    } else if (dataObject && dataObject.hasOwnProperty('technician_id')) {
      this.technicianFields.push(key);
    }

    if (key === 'headsubsidiary') {
      value =
        (dataObject &&
          ((dataObject as CompanyData).headoffice === 'true'
            ? 'hauptbetrieb'
            : (dataObject as CompanyData).subsidiary === 'true'
            ? 'nebenbetrieb'
            : (dataObject as CompanyData).auxiliary_company === 'true'
            ? 'auxiliary_company'
            : '')) ||
        '';
    } else {
      value = (dataObject && dataObject[key]) || '';
    }

    const validators = [Validators.required];
    if (key === 'postcode') {
      validators.push(ValidationsService.maxLengthValidator());
    }

    return key === 'tax_id' || (key === 'email' && !this.myDataFormFields.isEmailRequired)
      ? new FormControl({
          value,
          disabled: this.isFormReadonly,
        })
      : new FormControl(
          {
            value,
            disabled: this.isFormReadonly,
          },
          validators
        );
  }

  private updateFormWithCompanyData(companies: Companies): void {
    if (companies && companies.company !== null && companies.company.length > 0) {
      const companyData = companies.company[0];

      this.companyId = companyData.company_id;
      if (companyData.headoffice === 'true') {
        companyData.headsubsidiary = 'hauptbetrieb';
      }
      if (companyData.subsidiary === 'true') {
        companyData.headsubsidiary = 'nebenbetrieb';
      }
      if (this.showAuxiliaryOption && companyData.auxiliary_company === 'true') {
        companyData.headsubsidiary = 'auxiliary_company';
      }
      this.technicianInfoFormGroup.patchValue(companyData);
    } else {
      this.companyId = '';
      this.companyFields.forEach(key => {
        if (key !== 'tax_id') {
          this.technicianInfoFormGroup.controls[key].reset();
        }
      });
    }
  }

  private updateTechnicianApplicationData() {
    const currentFormData = this.technicianInfoFormGroup.value;

    if (currentFormData.birthday) {
      const birthday = currentFormData.birthday;
      currentFormData.birthday = this.commonService.parseDateForSaving(birthday);
    }
    const technicianApplicationData: UpdateTechnicianApplication = {
      company: {
        company_id: this.companyId,
        company_name: currentFormData.company_name,
        street: currentFormData.street,
        street_number: currentFormData.street_number,
        postcode: currentFormData.postcode,
        city: currentFormData.city,
        telephone: currentFormData.telephone,
        ceo: currentFormData.ceo,
        headoffice: currentFormData.headsubsidiary === 'hauptbetrieb' ? 'true' : 'false',
        subsidiary: currentFormData.headsubsidiary === 'nebenbetrieb' ? 'true' : 'false',
        ...(this.showAuxiliaryOption && {
          auxiliary_company: currentFormData.headsubsidiary === 'auxiliary_company' ? 'true' : 'false',
        }),
        ...(this.showCompanyDescription && { company_description: currentFormData.company_description }),
      },
      technician: {
        technician_id: currentFormData.technician_id,
        last_name: currentFormData.last_name,
        first_name: currentFormData.first_name,
        birthday: currentFormData.birthday,
        sk_title: currentFormData.title || null,
        telephone: currentFormData.user_telephone,
        email: currentFormData.user_mail,
        ...(this.showGuildMember && { is_guild_member: currentFormData.is_guild_member ? 'true' : null }),
      },
    };
    this.technicianApplicationDataChange.emit({
      applicationData: technicianApplicationData,
      formInvalidState: this.technicianInfoFormGroup.invalid,
    });
  }
}
